import React from "react";

interface CMANeighborhoodProfileProps {
  neighborhoodData: any;
  primaryColor: string;
  updatedAt?: string | null;
}

function demandColor(rating: number): string {
  if (rating >= 7) return "#16a34a"; // green
  if (rating >= 4) return "#d97706"; // amber
  return "#dc2626"; // red
}

function trendBadgeStyle(trend: string): React.CSSProperties {
  if (trend === "Rising") return { background: "#dcfce7", color: "#15803d" };
  if (trend === "Easing") return { background: "#fef3c7", color: "#b45309" };
  return { background: "#f1f5f9", color: "#475569" }; // Stable
}

export function CMANeighborhoodProfile({
  neighborhoodData,
  primaryColor,
  updatedAt,
}: CMANeighborhoodProfileProps) {
  const metrics = neighborhoodData?.metrics;
  const analysis = neighborhoodData?.analysis;

  if (!metrics) return null;

  const demandRating = analysis?.demandRating;
  const buyerTrend = analysis?.buyerTrend;
  const monthsOfInventory = metrics?.monthsOfInventory;
  const activeListings = metrics?.activeListings;
  const pendingListings = metrics?.pendingListings;
  const priceReductionPct = metrics?.priceReductionPct;
  const soldLast90 = metrics?.soldLast90Days ?? metrics?.soldCount;

  const daysAgo = updatedAt
    ? Math.round((Date.now() - new Date(updatedAt).getTime()) / 86_400_000)
    : null;

  return (
    <div className="space-y-4">
      {/* Row 1: 3 large stat cards */}
      <div className="grid grid-cols-3 gap-4">
        {/* Demand Rating */}
        {demandRating != null && (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 text-center">
            <p
              className="text-3xl md:text-4xl font-bold"
              style={{ color: demandColor(demandRating) }}
            >
              {demandRating}/10
            </p>
            <div className="mt-2 mx-auto w-full max-w-[80px] h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${(demandRating / 10) * 100}%`,
                  backgroundColor: demandColor(demandRating),
                }}
              />
            </div>
            <p className="text-sm text-slate-400 mt-2">Demand Rating</p>
          </div>
        )}

        {/* Buyer Trend */}
        {buyerTrend && (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 text-center flex flex-col items-center justify-center">
            <span
              className="text-sm font-semibold px-3 py-1.5 rounded-full"
              style={trendBadgeStyle(buyerTrend)}
            >
              {buyerTrend}
            </span>
            <p className="text-sm text-slate-400 mt-2">Buyer Trend</p>
          </div>
        )}

        {/* Months of Inventory */}
        {monthsOfInventory != null && (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 text-center">
            <p className="text-3xl md:text-4xl font-bold text-slate-900">
              {typeof monthsOfInventory === "number"
                ? monthsOfInventory.toFixed(1)
                : monthsOfInventory}
            </p>
            <p className="text-sm text-slate-400 mt-2">Months of Inventory</p>
          </div>
        )}
      </div>

      {/* Row 2: 4 small stats */}
      {(activeListings != null ||
        pendingListings != null ||
        priceReductionPct != null ||
        soldLast90 != null) && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-slate-100">
            {activeListings != null && (
              <div className="p-5 text-center">
                <p className="text-2xl font-bold text-slate-900">{activeListings}</p>
                <p className="text-xs text-slate-400 mt-1">Active Listings</p>
              </div>
            )}
            {pendingListings != null && (
              <div className="p-5 text-center">
                <p className="text-2xl font-bold text-slate-900">{pendingListings}</p>
                <p className="text-xs text-slate-400 mt-1">Pending</p>
              </div>
            )}
            {priceReductionPct != null && (
              <div className="p-5 text-center">
                <p className="text-2xl font-bold text-slate-900">
                  {typeof priceReductionPct === "number"
                    ? `${priceReductionPct.toFixed(0)}%`
                    : priceReductionPct}
                </p>
                <p className="text-xs text-slate-400 mt-1">Price Reductions</p>
              </div>
            )}
            {soldLast90 != null && (
              <div className="p-5 text-center">
                <p className="text-2xl font-bold text-slate-900">{soldLast90}</p>
                <p className="text-xs text-slate-400 mt-1">Sold (90 days)</p>
              </div>
            )}
          </div>
        </div>
      )}

      {daysAgo != null && (
        <p className="text-xs text-slate-400 text-right">
          Updated {daysAgo === 0 ? "today" : `${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`}
        </p>
      )}
    </div>
  );
}
